import React, { useState, useEffect } from 'react'
import { Input, CompanyPicker, TextareaAutosize, Select, formatSelectOptions } from '../../../../components'
import { RelationTagsEditor } from '../RelationTagsEditor'
import { personService } from '../../../../services'
import { useGlobalState } from '../../../../hooks'

let rowKeyCounter = 0

const emptyRow = (defaults) => ({
  key: ++rowKeyCounter,
  person_name: '',
  person_email: '',
  person_linkedin: '',
  person_notes: '',
  company_uuid: defaults.company_uuid,
  company_name: defaults.company_name,
  relation_type_ids: [...defaults.relation_type_ids],
  kayenta_contact_user_ids: [...(defaults.kayenta_contact_user_ids || [])],
})

const val = (e) => (e.target !== undefined ? e.target.value : e.value)

// Bulk-create several persons at once (Nätverk "Lägg till flera"). A shared
// default company/relation set can be applied to all rows via "Använd för
// alla rader", then each row's own CompanyPicker/RelationTagsEditor can
// still be edited individually — the defaults only ever get copied in on
// demand, never silently re-synced, so per-row overrides aren't clobbered.
export const BulkPersonImport = ({ dialogRef, hideModalDialog, setLoadingModalDialog, onImported }) => {
  const [relationTypes, setRelationTypes] = useState([])
  const [defaultCompany, setDefaultCompany] = useState({ uuid: '', name: '' })
  const [defaultRelationIds, setDefaultRelationIds] = useState([])
  const [rows, setRows] = useState([emptyRow({ company_uuid: '', company_name: '', relation_type_ids: [], kayenta_contact_user_ids: [] })])
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)

  // Kayenta-kontakt is a batch-level default only — it applies to every row, unlike
  // company/relations which also have per-row controls. The API does accept a per-row
  // kayenta_contact_user_ids override, but the row table is already six columns wide and
  // an imported batch almost always shares the same contact.
  const [loggedInUser] = useGlobalState('loggedInUser')
  const [users, setUsers] = useState([])
  const [contactUserIds, setContactUserIds] = useState([])

  useEffect(() => {
    personService.getRelationTypes().then(res => setRelationTypes(res.relationTypes || []))
    personService.getUsers().then(res => setUsers(res.users || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!loggedInUser?.user_id || !users.length) return
    setContactUserIds(prev => {
      if (prev.length) return prev
      return users.some(u => u.user_id === loggedInUser.user_id) ? [loggedInUser.user_id] : prev
    })
  }, [loggedInUser, users])

  const toggleContact = (userId) => {
    setContactUserIds(prev => prev.some(id => String(id) === String(userId))
      ? prev.filter(id => String(id) !== String(userId))
      : [...prev, userId])
  }

  const setRow = (key, patch) => setRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r))
  const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key))

  const addRow = () => setRows(prev => [
    ...prev,
    emptyRow({ company_uuid: defaultCompany.uuid, company_name: defaultCompany.name, relation_type_ids: defaultRelationIds, kayenta_contact_user_ids: contactUserIds }),
  ])

  const applyDefaultsToAllRows = () => {
    setRows(prev => prev.map(r => ({
      ...r,
      company_uuid: defaultCompany.uuid,
      company_name: defaultCompany.name,
      relation_type_ids: [...defaultRelationIds],
      kayenta_contact_user_ids: [...contactUserIds],
    })))
  }

  const toggleRowContact = (key, userId) => setRows(prev => prev.map(r => {
    if (r.key !== key) return r
    const current = r.kayenta_contact_user_ids || []
    return {
      ...r,
      kayenta_contact_user_ids: current.some(id => String(id) === String(userId))
        ? current.filter(id => String(id) !== String(userId))
        : [...current, userId],
    }
  }))

  const submit = () => {
    setError(null)

    const validRows = rows.filter(r => r.person_name && (r.person_email || r.person_linkedin))
    if (!validRows.length) {
      setError('Minst en rad med namn och e-post/LinkedIn krävs')
      return Promise.resolve()
    }
    // Each row must end up with a contact — either its own, or the batch default it falls
    // back to. Mirrors the server rule in controllerPerson.bulkInsert, which would
    // otherwise silently drop such a row into skipped[].
    const rowsMissingContact = validRows.filter(r => !(r.kayenta_contact_user_ids || []).length && !contactUserIds.length)
    if (rowsMissingContact.length) {
      setError(validRows.length === rowsMissingContact.length
        ? 'Minst en Kayenta-kontakt krävs — välj för alla rader eller per rad'
        : `Kayenta-kontakt saknas för: ${rowsMissingContact.map(r => r.person_name).join(', ')}`)
      return Promise.resolve()
    }

    setLoadingModalDialog(true)
    return personService.bulkCreate(validRows.map(r => ({
      key: r.key, // round-trips through the API's skipped[].row so we can match rows back below; ignored server-side otherwise
      person_name: r.person_name,
      person_email: r.person_email || undefined,
      person_linkedin: r.person_linkedin || undefined,
      person_notes: r.person_notes || undefined,
      company_uuid: r.company_uuid || undefined,
      company_name: r.company_uuid ? undefined : (r.company_name || undefined),
      relation_type_ids: r.relation_type_ids,
      // Omit when empty so the server falls back to the batch default rather than
      // treating an empty array as "this row wants no contacts".
      kayenta_contact_user_ids: (r.kayenta_contact_user_ids || []).length ? r.kayenta_contact_user_ids : undefined,
    })), contactUserIds)
      .then(res => {
        setLoadingModalDialog(false)
        onImported && onImported(res)

        if (!res.skipped || !res.skipped.length) {
          hideModalDialog()
          return
        }

        // Partial success — keep the dialog open with only the skipped rows
        // left to fix, so the user doesn't have to re-enter what worked.
        setSummary(res)
        const skippedKeys = new Set(res.skipped.map(s => s.row.key))
        setRows(prev => prev.filter(r => skippedKeys.has(r.key)))
      })
      .catch(err => {
        setLoadingModalDialog(false)
        setError(err.message || 'Bulkimport misslyckades')
      })
  }

  if (dialogRef) dialogRef.current = { submit }

  return (
    <div className="network-form bulk-person-import">
      {error && <div className="form-error">{error}</div>}
      {summary &&
        <div className="form-hint">
          {summary.created.length} skapades · {summary.skipped.length} hoppades över ({summary.skipped.map(s => s.reason).join(', ')})
        </div>
      }

      <div className="bulk-defaults">
        <div className="form-row">
          <CompanyPicker
            label="Standardföretag för alla rader"
            name="default_company"
            value={defaultCompany.uuid}
            onChange={({ value, company }) => setDefaultCompany({ uuid: value, name: company?.company_name || '' })}
            allowCreate
            isNullable
          />
          <div>
            <label>Standardrelationer för alla rader</label>
            <RelationTagsEditor relationTypes={relationTypes} selectedIds={defaultRelationIds} onChange={setDefaultRelationIds} />
          </div>
        </div>
        <div className="form-row">
          <Select
            name="kayenta_contact_user_ids"
            label={<>Kayenta-kontakt för alla rader <span className="label-required">*</span></>}
            title="Används för varje rad som inte har en egen Kayenta-kontakt."
            showMultiLabel
            isMulti
            placeholder="Välj Kayenta-person"
            value={contactUserIds.map(id => ({ value: id }))}
            options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
            onChange={e => toggleContact(e.target !== undefined ? e.target.value : e.value)}
          />
        </div>
        <button type="button" className="button" onClick={applyDefaultsToAllRows}>Använd för alla rader</button>
      </div>

      <table className="bulk-person-table">
        <thead>
          <tr>
            <th>Namn</th>
            <th>E-post</th>
            <th>LinkedIn</th>
            <th>Företag</th>
            <th>Kayenta-kontakt</th>
            <th>Relationer</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => ([
            <tr key={row.key} className="bulk-person-row">
              <td><Input name={`person_name-${row.key}`} value={row.person_name} onChange={e => setRow(row.key, { person_name: val(e) })} /></td>
              <td><Input name={`person_email-${row.key}`} value={row.person_email} onChange={e => setRow(row.key, { person_email: val(e) })} /></td>
              <td><Input name={`person_linkedin-${row.key}`} value={row.person_linkedin} onChange={e => setRow(row.key, { person_linkedin: val(e) })} /></td>
              <td>
                <CompanyPicker
                  name={`company_uuid-${row.key}`}
                  value={row.company_uuid}
                  onChange={({ value, company }) => setRow(row.key, { company_uuid: value, company_name: company?.company_name || '' })}
                  allowCreate
                  isNullable
                />
              </td>
              <td>
                <Select
                  name={`kayenta_contact_user_ids-${row.key}`}
                  showMultiLabel
                  isMulti
                  /* Empty row = "use the batch default", so the placeholder says so rather
                     than "välj". "Standard" both fits the narrow column without truncating
                     and matches the Standardföretag/Standardrelationer labels above. */
                  placeholder={contactUserIds.length ? 'Standard' : 'Välj person'}
                  value={(row.kayenta_contact_user_ids || []).map(id => ({ value: id }))}
                  options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
                  onChange={e => toggleRowContact(row.key, e.target !== undefined ? e.target.value : e.value)}
                />
              </td>
              <td>
                <RelationTagsEditor
                  relationTypes={relationTypes}
                  selectedIds={row.relation_type_ids}
                  onChange={(ids) => setRow(row.key, { relation_type_ids: ids })}
                />
              </td>
              <td>
                {rows.length > 1 &&
                  <button type="button" className="button-icon" title="Ta bort rad" onClick={() => removeRow(row.key)}>✕</button>
                }
              </td>
            </tr>,
            <tr key={`${row.key}-notes`} className="bulk-person-row-notes">
              <td colSpan={7}>
                <label>Anteckningar</label>
                <TextareaAutosize
                  name={`person_notes-${row.key}`}
                  value={row.person_notes}
                  minRows={2}
                  onChange={e => setRow(row.key, { person_notes: val(e) })}
                />
              </td>
            </tr>,
          ]))}
        </tbody>
      </table>

      <button type="button" className="button" onClick={addRow}>+ Lägg till rad</button>
    </div>
  )
}
